import React, {useEffect} from "react";
import {Link} from "react-router-dom";
import {Controller, SubmitHandler, useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";

import AuthCard from "../../components/authCard/AuthCard";
import InputField from "../../components/inputField/InputField";
import StyledButton from "../../components/styledButton/StyledButton";
import {Loader} from "../../components/loading/Loader";

import {LoginFormData, loginFormSchema} from "./loginHelper";
import {useLogin} from "../../services/api/auth/login/useLogin";

import styles from "./Login.module.scss";

export default function Login() {
  const {
    handleSubmit,
    control,
    formState: {errors, isSubmitting},
  } = useForm<LoginFormData>({
    defaultValues: {
      email: "",
      password: "",
    },

    resolver: zodResolver(loginFormSchema),
  });
  const loginUser = useLogin();
  const {isPending} = loginUser;

  const title = "Log in";
  const subtitle = "Continue to ElitePass";

  const submit: SubmitHandler<LoginFormData> = async (formData) => {
    const payload = {data: {...formData}};
    await loginUser.mutateAsync(payload);
  };

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === "Enter") handleSubmit(submit);
    };
    document.addEventListener("keydown", handleKeyPress);

    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [submit]);

  const Help = () => (
    <div className={styles.footerContainer}>
      <p className={styles.text}>
        Forgot your password?{" "}
        <Link to="/resetPassword" className={styles.link}>
          Reset
        </Link>
      </p>
      <p className={styles.text}>
        New to ElitePass?{" "}
        <Link className={styles.link} to="/register">
          Create an account
        </Link>
      </p>
    </div>
  );

  if (isPending) {
    return <Loader type={"white"} />;
  }

  return (
    <AuthCard title={title} subtitle={subtitle}>
      <form className={styles.form} onSubmit={handleSubmit(submit)}>
        <Controller
          name="email"
          control={control}
          render={({field: {onChange, value}}) => (
            <InputField
              classname={styles.input}
              title="Email"
              value={value}
              setValue={onChange}
              error={errors.email?.message}
              type="text"
            />
          )}
        />
        <Controller
          name="password"
          control={control}
          render={({field: {onChange, value}}) => (
            <InputField
              classname={styles.input}
              title="Password"
              value={value}
              setValue={onChange}
              error={errors.password?.message}
              type="password"
            />
          )}
        />
        <StyledButton
          baseClassname={styles.button}
          text="Log in"
          disabled={isSubmitting}
          type={"submit"}
        />
      </form>
      <Help />
    </AuthCard>
  );
}
